/**
 * 视频节点该默认多长。
 *
 * 测试报告建议#2 的三条要求全部落在这一个决议链上：
 *
 *   ① 全局默认 + 节点级覆盖   → preferred 兜在链尾，nodeDuration 排在链首
 *   ② 按上游分镜节点自动适配   → upstreamNodes
 *   ③ 新建节点继承上一个视频节点 → lastUsed
 *
 * 报告的原话是「默认按 Seedance 2.0 的 15s 生成，但实际创作中 5s、10s、15s
 * 分段生成是常态（尤其特效密集段需要短镜头拆分）」。所以这里**不能**图省事
 * 取模型上限——那等于每个新节点都按最贵的档预估点数，用户还得每次手动调下来。
 *
 * ⚠️ 顺序不能改，它表达的是"谁的意图更近"：
 * 用户在这个节点上亲手选过的 > 这个节点接的那场戏要多长 > 他刚才在用的 > 他设过的默认。
 */

// 与后端 userPreferences 的 videoDuration 档位表必须一致：
// 界面上点得到、后端却判非法，用户会看到"保存失败"却不知道为什么。
export const VIDEO_DURATION_STEPS = [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30];

/**
 * 画布偏好的前端默认值，**必须与 backend/services/userPreferences.js 的
 * DEFAULTS.canvas 逐字段一致**（接口还没回来时界面先用它渲染，两边不一致
 * 会出现"刷新前后设置不一样"）。
 *
 * 放在这里而不是设置页里，是因为现在有两个消费方：设置页（改它）和画布
 * （用它）。留两份副本迟早漂开——设置页加了一项、画布读到 undefined，
 * 而这种漂移不报错，只是默默不生效。
 */
export const CANVAS_PREFERENCE_DEFAULTS = {
  performance: 'balanced',
  snapGrid: true,
  edgeMotion: true,
  openInspector: true,
  promptOptimize: true,
  imageRatio: '16:9',
  videoRatio: '16:9',
  videoDuration: 5,
  sound: true,
  autoAsset: false,
  agentMode: 'follow',
};

// 兜底默认。写 5 不写 15 的理由见文件头。
export const FALLBACK_VIDEO_DURATION = 5;

// 时长档位按模型的上限裁——Seedance 2.0 系列到 15 秒，2.5 到 30 秒。
// ⚠️ 写死成 [..., 15] 的话，选了 2.5 也点不到 20/25/30，它最大的卖点
//（30 秒连贯直出）在界面上根本够不着。上限从 /canvas/models 下发的
// 新目录从 selectables.duration 下发，旧目录仍可能使用 minDuration/maxDuration；
// 两种形态都要兼容，后端 normalizeVideoDuration 用的是同一组边界。
export function durationsFor(model) {
  const min = Number(model?.selectables?.duration?.min ?? model?.minDuration) || 4;
  const max = Number(model?.selectables?.duration?.max ?? model?.maxDuration) || 15;
  return VIDEO_DURATION_STEPS.filter(value => value >= min && value <= max);
}

// 从 2.5 切回 2.0 时，之前选的 30 秒对新模型是非法值。后端 normalizeVideoDuration
// 会钳回 15，但界面还高亮着 30、按钮上的点数也按 30 算——**显示一个价、实际扣另一个价**
// 正是 CLAUDE.md 点名要避免的那类不一致。切模型时一起收敛。
export function clampDuration(model, value) {
  const options = durationsFor(model);
  const min = options[0] || 4;
  const max = options[options.length - 1] || 15;
  return Math.min(max, Math.max(min, Number(value) || FALLBACK_VIDEO_DURATION));
}

// 设置页那两栏（默认图片比例 / 默认视频比例）给的取值范围，与后端
// userPreferences 的 imageRatio/videoRatio 白名单一致。
const PREFERABLE_RATIOS = ['9:16', '16:9', '1:1'];

/**
 * 新建节点的画幅兜底。
 *
 * 设置页早就有「默认图片比例/默认视频比例」两栏、还标着"个人默认"，
 * 但画布从来没读过偏好——用户改了什么都不会发生，那两栏至今是摆设。
 *
 * ⚠️ 要过白名单：偏好是存在库里的，历史脏值（或别处写坏的值）直接灌进画幅
 * 会让后端按非法比例提交，报错还得从"我明明没改过画幅"查起。
 */
export function defaultRatioFor(rawType, { imageRatio, videoRatio } = {}) {
  const preferred = rawType === 'image' ? imageRatio : videoRatio;
  return PREFERABLE_RATIOS.includes(preferred) ? preferred : '16:9';
}

const LAST_VIDEO_CONFIG_KEY = 'xy:canvas:lastVideoConfig';

// ⚠️ 沿用旧画布页那把 key，不另起一个。用户从旧页切过来时"上次用的时长"
// 就该接得上——换 key 等于让他从头再调一遍，而这正是建议#2 ③ 要消灭的动作。
export function readLastVideoConfig() {
  try { return JSON.parse(localStorage.getItem(LAST_VIDEO_CONFIG_KEY) || '{}') || {}; } catch { return {}; }
}

export function writeLastVideoConfig(patch) {
  try {
    const next = { ...readLastVideoConfig(), ...(patch || {}) };
    localStorage.setItem(LAST_VIDEO_CONFIG_KEY, JSON.stringify(next));
  } catch { /* 隐私模式等 localStorage 不可用：继承不了不是错误，别炸掉整个面板 */ }
}

/**
 * 上游那场戏要多长。
 *
 * ⚠️ **只认分镜节点**（`agent_role === 'shot_task'`），不要"随便找个上游的
 * duration"——音频节点的 `data.duration` 是这段音频本身的秒数（预置数据里就有
 * 4.8 这种值），拿它当视频时长会把一个 10 秒的镜头悄悄改成 5 秒（clamp 之后），
 * 而用户完全看不出是哪来的。宁可不适配，也不要适配错。
 */
export function upstreamShotDuration(upstreamNodes) {
  const shot = (Array.isArray(upstreamNodes) ? upstreamNodes : [])
    .find(node => node?.data?.agent_role === 'shot_task' && Number(node?.data?.duration) > 0);
  return shot ? Number(shot.data.duration) : null;
}

/**
 * ⚠️ 收的是 `upstreamDuration`（一个数），不是上游节点数组。
 * 抽取与决议分开，调用方才能把这个数放进 React 的依赖表——传数组的话
 * 每次渲染都是新引用，effect 每帧重跑，用户刚选的参数被反复重置。
 * 抽取用 upstreamShotDuration。
 *
 * @param {object} input
 *   nodeDuration     节点自己存着的时长（用户在这个节点上选过 → 最高优先）
 *   upstreamDuration 上游分镜那场戏要多长
 *   lastUsed         上一次用过的时长（localStorage）
 *   preferred        全局偏好 canvas.videoDuration
 *   model            当前模型，用来裁上限
 * @returns {number}
 */
export function resolveVideoDuration({ nodeDuration, upstreamDuration, lastUsed, preferred, model } = {}) {
  const picked = [nodeDuration, upstreamDuration, lastUsed, preferred]
    .map(Number)
    .find(value => Number.isFinite(value) && value > 0);
  return clampDuration(model, picked || FALLBACK_VIDEO_DURATION);
}
